import { useEffect, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useLocation } from "react-router-dom";

export const useAuthSession = () => {
  const { isSignedIn, isLoaded } = useAuth();
  const location = useLocation();
  const [pendingAuth, setPendingAuth] = useState<string | null>(null);

  // Store the sessionId when accessing auth route while not logged in
  useEffect(() => {
    const path = location.pathname;
    const authMatch = path.match(/^\/auth\/([\w-]+)$/);

    if (authMatch && !isSignedIn) {
      // Store the sessionId in localStorage to persist across page navigations
      const sessionId = authMatch[1];
      localStorage.setItem("PENDING_AUTH_SESSION_ID", sessionId);
      setPendingAuth(sessionId);
    }
  }, [location.pathname, isSignedIn]);

  const [initialCheckComplete, setInitialCheckComplete] = useState(false);

  // Check for stored sessionId on component mount
  useEffect(() => {
    if (initialCheckComplete) return;

    const storedSessionId = localStorage.getItem("PENDING_AUTH_SESSION_ID");
    if (storedSessionId) {
      setPendingAuth(storedSessionId);
    }
    setInitialCheckComplete(true);
  }, [initialCheckComplete]);

  // Handle session when signed in state changes
  useEffect(() => {
    if (!initialCheckComplete) return;

    const storedSessionId = localStorage.getItem("PENDING_AUTH_SESSION_ID");
    if (storedSessionId && isSignedIn) {
      // If logged in and we have a stored session ID, redirect and clear storage
      setPendingAuth(storedSessionId);
      // We'll clear this in another effect after navigation happens
    } else if (storedSessionId && !isSignedIn) {
      // If not logged in but we have a stored ID, keep it in state
      setPendingAuth(storedSessionId);
    }
  }, [isSignedIn, initialCheckComplete]);

  // Add an effect to clear localStorage after successful navigation to auth route
  useEffect(() => {
    if (
      isSignedIn &&
      pendingAuth &&
      location.pathname.includes(`/auth/${pendingAuth}`)
    ) {
      // We've successfully navigated to the auth route - clear the stored session ID
      localStorage.removeItem("PENDING_AUTH_SESSION_ID");
      // Wait to clear state until after rendering to prevent immediate re-render issues
      setTimeout(() => setPendingAuth(null), 100);
    } else if (
      !isSignedIn &&
      pendingAuth &&
      !location.pathname.includes("/auth/")
    ) {
      // If the user signed out while having a pending auth and is not on the auth route,
      // maintain the state but don't navigate yet (wait for them to sign in again)
    }
  }, [isSignedIn, pendingAuth, location.pathname]);

  // Cleanup effect - if user navigates away or closes tab, cleanup localStorage
  useEffect(() => {
    // Cleanup function will run when component unmounts or on dependency changes
    return () => {
      if (pendingAuth) {
        localStorage.removeItem("PENDING_AUTH_SESSION_ID");
      }
    };
  }, [pendingAuth]);

  return { pendingAuth, isSignedIn, isLoaded };
};
